import { ShipmentSource } from 'app/shared/models/shipments/shipment-source.enum';
import dayjs from 'dayjs';
import { PhysicalShipmentStatus, ShipmentStatus, Source, UPSPickUpType } from '../../shared/enum/general-enum';
import { IAccessPoint } from './access-point.interface';
import { Commodity } from './commodity.model';
import { Contact } from './contact.model';
import { IDeliveryDefenseScore } from './delivery-defense-score.interface';
import { EstimatorDetail } from './estimator-detail.model';
import { Estimator } from './estimator.model';

export class Package {
    AccessorialCharges = null;
    AccessPoint: IAccessPoint = null;
    AddToExpress = false;
    AESCitation = '';
    AESCitationType = '';
    BillToAccount = '';
    BillToCustomerId = '';
    BillToThirdPartyCountry = '';
    BillToThirdPartyPostalCode = '';
    BillDutiesTo = '';
    BillDutiesToAccount = '';
    BillDutiesToThirdPartyCountry = '';
    BillDutiesToThirdPartyPostalCode = '';
    Branch = 'LAX';
    BrokerAccountNo = '';
    BrokerAddress1 = '';
    BrokerAddress2 = '';
    BrokerCity = '';
    BrokerCompanyName = '';
    BrokerContactName = '';
    BrokerCountryCode = '';
    BrokerPhoneNumber = '';
    BrokerPostalCode = '';
    BrokerStateProvince = '';
    BrokerTaxID = '';
    BusinessDaysInTransit = '';
    CarrierCode = 0;
    CodAmount = 0;
    CodIsCancelled = false;
    CODTrackingNumber = '';
    Commodities: Commodity[] = [];
    CommodityDescription = '';
    CommodityPhotoS3Url = '';
    ConfirmationNumber = '';
    CoverageCurrencyCode = '';
    CreateDate = '';
    CustomerId = 'NOID';
    CustomerReferenceNumber = '';
    CustomField = '';
    CustomFieldValue = '';
    CustomsCurrencyCode = 'USD';
    CustomsTariffCode = '';
    DDScore: IDeliveryDefenseScore = null;
    DDSearch?: boolean = null;
    DDSearchStatus?: number; // 0: no score, no charge; 1: has score, charge; 2: from cache, no charge.
    DestinationControlStatement = '';
    DispatchConfirmationNumber = '';
    DispatchLocation = '';
    DispatchStatus = 0;
    DummyId = '';
    EeiFilingOption? = '';
    ErrorMessage = '';
    EstimatedCost = null;
    Estimator: Estimator[];
    EstimatorDetail: EstimatorDetail[];
    FedexZone = '';
    FilingOption: number | string = '0';
    FTRExemption = '';
    FuelSurchargeRate = null;
    FuelSurcharges = null;
    HasErrors = false;
    HasPickUpErrors = false;
    HasSED = false;
    Height = 0;
    HoldAtAddress1 = '';
    HoldAtAddress2 = '';
    HoldAtCity = '';
    HoldAtCountry = '';
    HoldAtLocationID = '';
    HoldAtState = '';
    HoldAtZip = '';
    InitialTrackingNumber = null;
    InsuranceCost = null;
    InsuredValue = 0;
    InsuredValueMultiplier = 1.0;
    InsuredValueMultiplierEnabled = false;
    InsuredValueThreshold = 0.0;
    InternalTrackingNumber = '';
    IPAddress = '';
    IsBillToThirdParty = false;
    IsBrokerSelect = false;
    IsBusiness = false;
    IsCod = false;
    IsCommercialInvoice = false;
    CommercialInvoiceFileType = null;
    CommercialInvoiceMimeType = null;
    CommercialInvoiceType = null;
    CommercialInvoice = null;
    IsDeliveryConfirmation = false;
    IsDDO = false; // direct delivery only
    IsDirectSignature = false;
    IsDropoff = false;
    IsDropShipment = false;
    IsDummy = false;
    IsEstimatorRequest = false;
    IsHighValueReport = false;
    IsHighValueShipment = false;
    IsHoldAtLocation = false;
    IsInternational = false;
    IsNAFTACertificate = false;
    IsPickUpRequested = false;
    IsRegularPickUp = false;
    IsReturnAdultSignature = false;
    IsReturnDirectSignature = false;
    IsReturnShipment = false;
    IsReturnService = false;
    IsSaturdayDelivery = false;
    IsSaturdayPickUp = false;
    IsSavedQuote = false;
    IsSecuredCod = false;
    IsShipFromHasRestrictedWords = false;
    IsShipFromRestrictedZip = false;
    IsShipToHasRestrictedWords = false;
    IsShipToRestrictedZip = false;
    IsSmartPickUp = false;
    IsThermal = false;
    IsUknownAddress = false;
    LabelImage = '';
    LabelImageFull = '';
    Length = 0;
    MovementReferenceNumber = '';
    NotifyCustomShipmentRecipient = false;
    NotifyDeliveryRecipient = false;
    NotifyExceptionRecipient = false;
    NotifySender = false;
    NotifyRecipient = false;
    OptionalServices = true;
    OriginalInsuredValue = 0;
    OverMaxLimit = false;
    PackageCode = '';
    PackageCodeDesc = '';
    PackageContent = '';
    PackageMemo = '';
    PhysicalStatus = PhysicalShipmentStatus.BillingInfo;
    PickUpAtHour = '';
    PickUpAtMinute = '';
    PickUpByHour = '';
    PickUpByMinute = '';
    PickUpContactName = '';

    // [MV3-1429] Pickup date should be TODAY by default.
    PickUpDate: Date | string = dayjs().startOf('day').toDate();

    PickUpErrorMessage = '';
    PickUpTelephone = '';
    PointsComments = '';
    PointsEquivalent = 0;
    PointsPending = false;
    PointsPosted = false;
    QuoteId = 'NOID';
    ReceivedBy = '';
    ReceivedTime = '';
    ReferenceNumber = '';
    ReprintRequested = false;
    ReturnLabel = false;
    ReturnLabelScheduleDate: Date | string = dayjs().add(8, 'days').startOf('day').toDate();
    ReturnTrackingNumber: string;
    ReturnShipmentId = '';
    ReturnServiceCode = '';
    ReturnServiceDescription = '';
    ServiceCharges = null;
    ServiceCode = '';
    ServiceCodeDesc = '';
    ShipDate: Date | string = dayjs().startOf('day').toDate();
    ShipFrom: Partial<Contact> = new Contact();
    ShipFromRerouting: Partial<Contact> = new Contact();
    ShipFromCountry = '';
    ShipmentDigest = '';
    ShipmentId = 'NOID';
    ShipmentPurpose = '';
    ShipmentReason = '';
    ShippingCost = null;
    ShipTo: Partial<Contact> = new Contact();
    ShipToRerouting: Partial<Contact> = new Contact();
    ShipToResidential = false;
    SignedAESAgreement = false;
    SkipDeliveryDefense = false;
    Source: Source | ShipmentSource = Source.PPIWeb;
    Status = ShipmentStatus.BillingInfo;
    TemplateID = '';
    TotalCharges = null;
    TotalCustomsValue = '';
    TrackingNumber = '';
    TransportationCharges = null;
    UpdateAddressBook = false;
    UPSPickUpType = UPSPickUpType.Occasional;
    UpsShipperNumber = '';
    UseAccessPoint = false;
    UseAccessPointCode = false;
    UseDeliveryDefense = false;
    UserId = 0;
    USPSProductID = 0;
    USPSQuoteID = '';
    USPSZone = 0;
    VoidedByUserId = 0;
    Weight = 0;
    WeightUOM = '';
    Width = 0;
    Zone = '';
    ShipToVAT = '';
    ShipToTaxID = '';
    ShipToEORI = '';
    ShipFromVAT = '';
    ShipFromTaxID = '';
    ShipFromEORI = '';
}
